import React, { useState, useCallback, useEffect } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Upload,
  FileText,
  Sparkles,
  Check,
  X,
  AlertCircle,
  Loader2,
  Package,
  Eye,
  Download,
  Trash2,
  Edit,
  Save,
  List,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CatalogImportAIPage = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  
  // Listino
  const [existingPricelists, setExistingPricelists] = useState([]);
  const [selectedPricelistId, setSelectedPricelistId] = useState('new');
  const [newPricelistName, setNewPricelistName] = useState('');
  
  // Risultati
  const [analysisResult, setAnalysisResult] = useState(null);
  const [extractedProducts, setExtractedProducts] = useState([]);
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [importResult, setImportResult] = useState(null);
  
  // Editing
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedProduct, setEditedProduct] = useState(null);

  // Carica listini esistenti
  useEffect(() => {
    fetchPricelists();
  }, []);

  const fetchPricelists = async () => {
    try {
      const res = await axios.get(`${API}/pricelists`);
      setExistingPricelists(res.data || []);
    } catch (error) {
      console.error('Error fetching pricelists:', error);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (selectedFile) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error('Tipo file non supportato. Usa PDF, JPG, PNG o WEBP');
      return;
    }
    
    if (selectedFile.size > 30 * 1024 * 1024) {
      toast.error('File troppo grande. Max 30MB');
      return;
    }
    
    setFile(selectedFile);
    setAnalysisResult(null);
    setExtractedProducts([]);
    setImportResult(null);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/catalog-import/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAnalysisResult(res.data);
      toast.success('Analisi completata!');
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'analisi"));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleExtract = async () => {
    if (!file) return;
    
    setExtracting(true);
    const formData = new FormData();
    formData.append('file', file);
    if (additionalInstructions) {
      formData.append('additional_instructions', additionalInstructions);
    }
    
    try {
      const res = await axios.post(`${API}/catalog-import/extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000 // 3 minuti timeout
      });
      
      setExtractedProducts(res.data.products || []);
      setSupplierInfo(res.data.supplier_info);
      
      if (res.data.products?.length > 0) {
        toast.success(`${res.data.products.length} prodotti estratti!`);
      } else {
        toast.warning('Nessun prodotto estratto. Verifica il file.');
      }
    } catch (error: any) {
      console.error('Extract error:', error);
      toast.error(getErrorMessage(error, 'Errore nell\'estrazione'));
    } finally {
      setExtracting(false);
    }
  };

  const handleImport = async () => {
    if (extractedProducts.length === 0) return;
    
    // Verifica nome listino
    if (selectedPricelistId === 'new' && !newPricelistName.trim()) {
      toast.error('Inserisci il nome del nuovo listino');
      return;
    }
    
    setImporting(true);
    try {
      // Determina il nome del listino
      const pricelistName = selectedPricelistId === 'new' 
        ? newPricelistName.trim() 
        : existingPricelists.find(p => p.id === selectedPricelistId)?.name || 'Listino Importato';
      
      const res = await axios.post(`${API}/catalog-import/import`, {
        products: extractedProducts,
        pricelist_id: selectedPricelistId === 'new' ? null : selectedPricelistId,
        pricelist_name: pricelistName,
        create_pricelist: true
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      setImportResult(res.data);
      
      if (res.data.imported > 0) {
        toast.success(`${res.data.imported} prodotti importati nel listino "${pricelistName}"!`);
      }
      
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} errori durante l'importazione`);
      }
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(getErrorMessage(error, 'Errore nell\'importazione'));
    } finally {
      setImporting(false);
    }
  };

  const removeProduct = (index) => {
    setExtractedProducts(prev => prev.filter((_, i) => i !== index));
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditedProduct({ ...extractedProducts[index] });
  };

  const saveEdit = () => {
    if (editingIndex !== null && editedProduct) {
      setExtractedProducts(prev => {
        const updated = [...prev];
        updated[editingIndex] = editedProduct;
        return updated;
      });
      setEditingIndex(null);
      setEditedProduct(null);
    }
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditedProduct(null);
  };

  return (
    <div className="space-y-6" data-testid="catalog-import-ai-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-primary" />
            Importazione Catalogo AI
          </h1>
          <p className="text-muted-foreground mt-1">
            Carica un listino PDF o immagine e lascia che l'AI estragga automaticamente i prodotti
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Section */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Carica Listino
            </CardTitle>
            <CardDescription>
              PDF, JPG, PNG o WEBP (max 30MB)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Dropzone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                ${dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
                ${file ? 'bg-green-50 border-green-300 dark:bg-green-950/20 dark:border-green-700' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
              />
              
              {file ? (
                <div className="space-y-2">
                  <FileText className="h-12 w-12 mx-auto text-green-600" />
                  <p className="font-medium text-green-700 dark:text-green-400">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setAnalysisResult(null);
                      setExtractedProducts([]);
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Rimuovi
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="font-medium">Trascina qui il file</p>
                  <p className="text-sm text-muted-foreground">oppure clicca per selezionare</p>
                </div>
              )}
            </div>

            {/* Additional Instructions */}
            <div className="space-y-2">
              <Label>Istruzioni aggiuntive (opzionale)</Label>
              <Textarea
                placeholder="Es: I prezzi sono senza IVA, la categoria è nella prima colonna..."
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
                rows={3}
              />
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button 
                className="w-full" 
                onClick={handleExtract}
                disabled={!file || extracting}
              >
                {extracting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Estrazione in corso...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Estrai Prodotti con AI
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleAnalyze}
                disabled={!file || analyzing}
              >
                {analyzing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analisi...
                  </>
                ) : (
                  <>
                    <Eye className="mr-2 h-4 w-4" />
                    Solo Analisi Struttura
                  </>
                )}
              </Button>
            </div>

            {/* Analysis Result */}
            {analysisResult && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <h4 className="font-medium">Analisi Struttura</h4>
                <div className="text-sm space-y-1">
                  <p><strong>Tipo:</strong> {analysisResult.document_type || 'N/D'}</p>
                  <p><strong>Fornitore:</strong> {analysisResult.supplier_name || 'N/D'}</p>
                  <p><strong>Prodotti stimati:</strong> {analysisResult.total_products_estimated || '?'}</p>
                  <p><strong>Qualità dati:</strong> {analysisResult.quality_score || '?'}/10</p>
                  {analysisResult.categories_found && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {analysisResult.categories_found.slice(0, 5).map((cat, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{cat}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Products Preview */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Prodotti Estratti
                {extractedProducts.length > 0 && (
                  <Badge variant="secondary">{extractedProducts.length}</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Rivedi e modifica i prodotti prima dell'importazione
              </CardDescription>
            </div>
            
            {extractedProducts.length > 0 && !importResult && (
              <Button onClick={handleImport} disabled={importing}>
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importazione...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Importa {extractedProducts.length} Prodotti
                  </>
                )}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {supplierInfo && (
              <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm">
                  <strong>Fornitore rilevato:</strong> {supplierInfo.name || 'Non identificato'}
                </p>
                {supplierInfo.detected_categories && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {supplierInfo.detected_categories.map((cat, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{cat}</Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Selezione Listino */}
            {extractedProducts.length > 0 && !importResult && (
              <div className="mb-4 p-4 border rounded-lg bg-primary/5 space-y-4">
                <div className="flex items-center gap-2">
                  <List className="h-5 w-5 text-primary" />
                  <h4 className="font-medium">Listino di destinazione</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Seleziona listino</Label>
                    <Select 
                      value={selectedPricelistId} 
                      onValueChange={(value) => {
                        setSelectedPricelistId(value);
                        if (value !== 'new') {
                          setNewPricelistName('');
                        }
                      }}
                    >
                      <SelectTrigger data-testid="pricelist-select">
                        <SelectValue placeholder="Seleziona o crea nuovo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">
                          <span className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            Crea nuovo listino
                          </span>
                        </SelectItem>
                        {existingPricelists.map((pl) => (
                          <SelectItem key={pl.id} value={pl.id}>
                            {pl.name} {pl.is_standard && <Badge className="ml-2 text-xs">Standard</Badge>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {selectedPricelistId === 'new' && (
                    <div className="space-y-2">
                      <Label>Nome nuovo listino *</Label>
                      <Input
                        placeholder="Es: Listino Pregis Gennaio 2026"
                        value={newPricelistName}
                        onChange={(e) => setNewPricelistName(e.target.value)}
                        data-testid="new-pricelist-name"
                      />
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  {selectedPricelistId === 'new' 
                    ? 'Verrà creato un nuovo listino con i prodotti e prezzi estratti'
                    : 'I prodotti verranno aggiunti/aggiornati nel listino selezionato'}
                </p>
              </div>
            )}
            
            {extractedProducts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>Carica un listino e clicca "Estrai Prodotti con AI"</p>
                <p className="text-sm mt-1">L'AI analizzerà il documento ed estrarrà automaticamente i prodotti</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">Codice</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead className="w-20">U.M.</TableHead>
                      <TableHead className="w-24 text-right">Prezzo</TableHead>
                      <TableHead className="w-20 text-right">IVA</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {extractedProducts.map((product, index) => (
                      <TableRow key={index}>
                        {editingIndex === index ? (
                          <>
                            <TableCell>
                              <Input
                                value={editedProduct.code || ''}
                                onChange={(e) => setEditedProduct({...editedProduct, code: e.target.value})}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={editedProduct.name || ''}
                                onChange={(e) => setEditedProduct({...editedProduct, name: e.target.value})}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={editedProduct.category || ''}
                                onChange={(e) => setEditedProduct({...editedProduct, category: e.target.value})}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={editedProduct.unit || ''}
                                onChange={(e) => setEditedProduct({...editedProduct, unit: e.target.value})}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={editedProduct.price || 0}
                                onChange={(e) => setEditedProduct({...editedProduct, price: parseFloat(e.target.value)})}
                                className="h-8 text-right"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={editedProduct.vat_rate || 22}
                                onChange={(e) => setEditedProduct({...editedProduct, vat_rate: parseFloat(e.target.value)})}
                                className="h-8 text-right"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={saveEdit}>
                                  <Save className="h-4 w-4 text-green-600" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={cancelEdit}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="font-mono text-xs">{product.code || '-'}</TableCell>
                            <TableCell className="font-medium">{product.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {product.category || 'N/D'}
                              </Badge>
                            </TableCell>
                            <TableCell>{product.unit || 'PZ'}</TableCell>
                            <TableCell className="text-right font-medium">
                              €{(product.price || 0).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {product.vat_rate || 22}%
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(index)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeProduct(index)}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Import Result */}
            {importResult && (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <h4 className="font-medium text-green-700 dark:text-green-400 flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  Importazione Completata
                </h4>
                <div className="mt-2 text-sm space-y-1">
                  <p><strong>Importati:</strong> {importResult.imported}</p>
                  <p><strong>Saltati:</strong> {importResult.skipped}</p>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-red-600"><strong>Errori:</strong></p>
                      <ul className="list-disc list-inside text-xs text-red-500">
                        {importResult.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <Button className="mt-4" onClick={() => navigate('/catalog')}>
                  Vai al Catalogo
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CatalogImportAIPage;
